<template>
    <div
        ref="root"
        class="checkout-modal"
        :class="{
            'checkout-modal--enter': enter,
            'checkout-modal--ready': ready,
        }"
        role="dialog"
        aria-modal="true"
        aria-label="Checkout"
    >
        <!--
            Click-outside dismisses, the way every other customer-facing modal
            in this app does — they are plain Bootstrap modals, and only the
            seller-side editors opt out with data-backdrop="static". This one
            was the odd one out.

            Armed on mousedown so a drag that starts inside the panel and
            releases out here does not count as a click outside. Bootstrap has
            that bug; a customer selecting text in the checkout should not lose
            it by overshooting.
        -->
        <div
            class="checkout-modal__backdrop"
            aria-hidden="true"
            @mousedown="onBackdropMouseDown"
            @click="onBackdropClick"
        ></div>
        <div class="checkout-modal__panel">
            <!--
                Fallback only. The checkout draws its own close control, which
                knows what is on screen — a sheet, a document, the step itself —
                and closes that rather than always tearing the whole thing down.
                It says so with a `chrome` envelope once it is up, and this
                button disappears.

                Until then it stays: a customer facing a checkout with no way
                out is worse than one facing two, and an old build, a boot
                failure or a fatal error never sends the envelope at all.
            -->
            <button
                v-if="checkoutOrigin && parentOwnsClose"
                type="button"
                class="checkout-modal__close"
                aria-label="Close checkout"
                @click="onCloseClick"
            >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                </svg>
            </button>

            <iframe
                v-if="checkoutOrigin"
                ref="iframe"
                :src="iframeSrc"
                class="checkout-modal__iframe"
                allow="payment; clipboard-write"
                referrerpolicy="strict-origin-when-cross-origin"
                title="Checkout"
            />

            <div
                v-if="checkoutOrigin"
                class="checkout-modal__loading"
                :aria-hidden="ready ? 'true' : 'false'"
                role="status"
                aria-live="polite"
            >
                <div class="checkout-modal__spinner" aria-hidden="true"></div>
                <span class="checkout-modal__sr">Loading checkout</span>
            </div>

            <div v-if="!checkoutOrigin" class="checkout-modal__misconfigured">
                Checkout is not available right now.
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

import { createCheckoutIframeBridge } from "../checkoutIframeBridge.js";

function readMeta(name) {
    const el = document.querySelector(`meta[name="${name}"]`);
    return el ? el.getAttribute("content") || "" : "";
}

const props = defineProps({
    quoteKey: { type: String, required: true },
    locale: { type: String, default: "sv" },
    selectedProductId: { type: [Number, String], default: null },
    explicitProductSelection: { type: Boolean, default: false },
    selectedKind: {
        type: String,
        default: null,
        validator: (v) => v === null || v === "product" || v === "option",
    },
    // Boot the checkout into its "preparing your signature" wait: this open is
    // a BankID app-switch return whose signing callback may not have landed.
    pendingSignature: { type: Boolean, default: false },
    // The BankID order the wait watches, so a cancelled order exits it early.
    pendingOrderRef: { type: String, default: null },
    // This open is the return leg of a Brite AIS app switch (deeplink_redirect):
    // the checkout resumes the bank-details assist instead of starting over.
    pendingAisResume: { type: Boolean, default: false },
});

const emit = defineEmits([
    "ready",
    "success",
    "complete",
    "failure",
    "cancel",
    "signed",
    "step",
    "telemetry",
    "fatal",
]);

const root = ref(null);
const iframe = ref(null);

// `checkoutOrigin` is read from a meta tag on mount rather than a prop so the
// iframe URL can be built from the server-rendered origin without it leaking
// into the component's public props.
const checkoutOrigin = ref("");
const bridge = ref(null);
// `enter` triggers the entry animation on the next paint after
// mount. `ready` flips when source/ posts the ready envelope —
// that's when we fade out the spinner and reveal the iframe.
const enter = ref(false);
const ready = ref(false);
const previousBodyOverflow = ref("");
// Whether we still draw the close button. Flipped off when the checkout claims
// it via the `chrome` envelope; see the template comment above the button.
const parentOwnsClose = ref(true);

const iframeSrc = computed(() => {
    const params = new URLSearchParams({
        quoteKey: props.quoteKey,
        parentOrigin: window.location.origin,
    });
    if (props.selectedProductId !== null && props.selectedProductId !== "") {
        params.set("selectedProductId", String(props.selectedProductId));
    }
    if (props.selectedKind) {
        params.set("selectedKind", props.selectedKind);
    }
    // A quote-page CTA asks the customer to confirm their offer again, even
    // when an earlier payment is unfinished. App-switch returns still resume.
    if (!props.pendingSignature && !props.pendingAisResume) {
        params.set("entry", "selection");
    }
    if (props.pendingSignature) {
        params.set("pendingSignature", "1");
        if (props.pendingOrderRef) {
            params.set("pendingOrderRef", props.pendingOrderRef);
        }
    }
    if (props.pendingAisResume) {
        params.set("pendingAis", "1");
    }
    if (readMeta("viewer-is-seller") === "1") {
        params.set("viewerIsSeller", "1");
    }
    const sellerToken = readMeta("checkout-seller-token");
    if (sellerToken) {
        params.set("sellerToken", sellerToken);
    }
    return `${checkoutOrigin.value}/?${params.toString()}`;
});

function handleMessage(msg) {
    switch (msg.type) {
        case "ready": {
            const initPayload = { locale: props.locale };
            if (props.selectedProductId !== null && props.selectedProductId !== "") {
                initPayload.selectedProductId = Number(props.selectedProductId);
            }
            if (props.selectedKind) {
                initPayload.selectedKind = props.selectedKind;
            }
            // Defaults are hints; only a deliberate product CTA may replace
            // the saved package. Never forward this intent on an app return.
            if (props.explicitProductSelection && props.selectedKind === "product"
                && !props.pendingSignature && !props.pendingAisResume) {
                initPayload.explicitProductSelection = true;
            }
            bridge.value.send("init", initPayload);
            ready.value = true;
            emit("ready", msg.payload);
            break;
        }

        case "chrome":
            parentOwnsClose.value = msg.payload?.close !== "iframe";
            break;

        case "resize":
            // Iframe is full-viewport; height is driven by CSS now.
            break;

        case "redirect": {
            const url = String(msg.payload?.url || "");
            if (!url) return;
            if (msg.payload?.target === "top" && window.top) {
                window.top.location.replace(url);
            } else {
                window.location.replace(url);
            }
            break;
        }

        case "success":
            emit("success", msg.payload);
            break;

        case "complete":
            emit("complete", msg.payload);
            break;

        case "failure":
            emit("failure", msg.payload);
            break;

        case "cancel":
            emit("cancel", msg.payload);
            break;

        case "signed":
            emit("signed", msg.payload);
            break;

        case "step":
            emit("step", msg.payload);
            break;

        case "event":
            emit("telemetry", msg.payload);
            break;

        case "fatal":
            emit("fatal", msg.payload);
            break;

        default:
            break;
    }
}

function requestClose() {
    bridge.value?.send("close-request", {});
}

// Only reachable in the fallback state: a checkout that got far enough to
// claim the close affordance has already taken this button away. So whatever
// is in the iframe is a build that predates the `chrome` envelope, a boot that
// failed, or a frame that is wedged — none of which can be relied on to answer
// `close-request`. It is still sent, for the build that does handle it, but the
// modal comes down on this click rather than waiting on a reply that in every
// case this button is visible for is unlikely to arrive.
function onCloseClick() {
    requestClose();
    emit("cancel", { reason: "user_closed" });
}

// Whether the pointer went down on the backdrop itself, rather than being
// dragged out onto it from inside the panel.
const backdropArmed = ref(false);

function onBackdropMouseDown(event) {
    backdropArmed.value = event.target === event.currentTarget;
}

function onBackdropClick(event) {
    if (!backdropArmed.value || event.target !== event.currentTarget) return;
    backdropArmed.value = false;
    emit("cancel", { reason: "backdrop_dismissed" });
}

// Escape, for as long as focus has not moved into the iframe. Once it has, the
// keypress belongs to the checkout's document and never reaches ours — the
// checkout runs its own Escape handler on the same gesture, routed through its
// chrome stack, so a sheet closes before the checkout does. This half only
// covers the window between the modal opening and the customer's first click.
function onKeydown(event) {
    if (event.key !== "Escape") return;
    emit("cancel", { reason: "user_closed" });
}

onMounted(() => {
    checkoutOrigin.value = readMeta("checkout-iframe-origin");

    // Portal the modal root onto <body>. This escapes whichever stacking
    // context our parent (the BankID Bootstrap modal) lives in, so its
    // entry animation can't fight ours and the iframe overlay isn't
    // clipped or reparented mid-transition.
    if (root.value && root.value.parentNode !== document.body) {
        document.body.appendChild(root.value);
    }

    // Lock background scroll while the modal is up.
    previousBodyOverflow.value = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", onKeydown);

    if (!checkoutOrigin.value) {
        // Even in the misconfigured branch we still want a smooth fade-in
        // so the message doesn't pop.
        requestAnimationFrame(() => {
            enter.value = true;
        });
        return;
    }

    // Two-frame deferral — the first frame mounts the element, the second
    // applies the `--enter` class. Without the rAF the browser collapses
    // both styles into the same paint and the transition doesn't run.
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            enter.value = true;
        });
    });

    nextTick(() => {
        bridge.value = createCheckoutIframeBridge({
            checkoutOrigin: checkoutOrigin.value,
            getIframeWindow: () => iframe.value?.contentWindow ?? null,
            onMessage: (msg) => handleMessage(msg),
        });
    });
});

onBeforeUnmount(() => {
    if (bridge.value) {
        bridge.value.destroy();
        bridge.value = null;
    }

    window.removeEventListener("keydown", onKeydown);
    document.body.style.overflow = previousBodyOverflow.value;

    // We portaled the element onto body manually, so Vue's unmount hook
    // can't reach it from its original parent — clean it up here.
    if (root.value && root.value.parentNode === document.body) {
        document.body.removeChild(root.value);
    }
});
</script>

<style lang="scss" scoped>
$ease-out: cubic-bezier(0.16, 1, 0.3, 1);

.checkout-modal {
    position: fixed;
    inset: 0;
    z-index: 2147483646;
    // Centre the panel so it reads as a modal on desktop; on mobile the panel
    // stretches to fill the viewport (see the panel rules below).
    display: flex;
    align-items: stretch;
    justify-content: center;
    // Subtle GPU compositing hint so the entry animation stays at 60fps.
    transform: translateZ(0);
}

// Dim scrim behind the modal. On desktop the panel is a centred ~1/3-width
// card, so the darkened backdrop frames it like a real modal; on mobile the
// panel fills the viewport and covers the scrim entirely. The modal we were
// launched from is dismissed on open (quoteCheckoutIframeModal.onShow), so it
// no longer needs a near-opaque scrim to hide a lingering modal behind it.
.checkout-modal__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(17, 17, 17, 0.6);
    // A slight defocus of the page behind. The scrim alone left the quote view
    // legible enough to still read as the thing in front of you, which is part
    // of why the panel read as a layer sitting on the page rather than a modal
    // lifted off it.
    backdrop-filter: blur(3px);
    opacity: 0;
    transition: opacity 220ms $ease-out;
}

.checkout-modal--enter .checkout-modal__backdrop {
    opacity: 1;
}

// Mobile-first: the panel fills the viewport (full-screen checkout). The
// desktop media query below shrinks it to a centred modal card.
.checkout-modal__panel {
    position: relative;
    width: 100%;
    height: 100%;
    background: #ffffff;
    overflow: hidden;
    opacity: 0;
    transform: translateY(12px);
    transition: opacity 280ms $ease-out, transform 280ms $ease-out;
}

// Desktop: a centred modal panel at roughly a third of the viewport width,
// clamped so it never gets too narrow or too wide to use comfortably, with
// room around it for the scrim to show through.
@media (min-width: 768px) {
    .checkout-modal {
        padding: 24px;
    }

    .checkout-modal__panel {
        width: clamp(420px, 33.3333vw, 560px);
        height: min(90vh, 900px);
        // Tighter than the 16px this started at. At 560px wide a 16px radius
        // reads as a soft card; a modal wants a crisper edge.
        border-radius: 10px;
        // Three layers rather than one soft blur: a contact shadow that seats
        // the panel, a mid spread that lifts it, and a wide ambient one for the
        // depth. One 60px blur at 28% spread the weight evenly and gave the
        // panel nothing to sit on.
        box-shadow:
            0 1px 2px rgba(17, 17, 17, 0.08),
            0 8px 24px rgba(17, 17, 17, 0.16),
            0 32px 64px rgba(17, 17, 17, 0.3);
        // Scales up rather than sliding up. A translate reads as the panel
        // shifting into place behind the page; growing from just under full
        // size reads as it coming toward the customer, which is the "pop" a
        // modal is supposed to have.
        transform: scale(0.96);
    }
}

.checkout-modal--enter .checkout-modal__panel {
    opacity: 1;
    transform: none;
}

.checkout-modal__iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
    background: transparent;
    opacity: 0;
    transition: opacity 220ms $ease-out;
}

// Cross-fade: spinner stays visible until ready, then iframe takes over.
.checkout-modal--ready .checkout-modal__iframe {
    opacity: 1;
}

.checkout-modal__loading {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    opacity: 1;
    transition: opacity 220ms $ease-out;
    pointer-events: none;
}

.checkout-modal--ready .checkout-modal__loading {
    opacity: 0;
}

.checkout-modal__spinner {
    width: 44px;
    height: 44px;
    border: 3px solid rgba(0, 0, 0, 0.08);
    border-top-color: rgba(0, 0, 0, 0.55);
    border-radius: 50%;
    animation: checkout-modal-spin 0.85s linear infinite;
}

@keyframes checkout-modal-spin {
    to { transform: rotate(360deg); }
}

.checkout-modal__close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 9999px;
    background: rgba(17, 17, 17, 0.06);
    color: #111;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: background 0.15s $ease-out, transform 0.15s $ease-out, opacity 220ms $ease-out;
    z-index: 2;

    &:hover {
        background: rgba(17, 17, 17, 0.12);
        transform: scale(1.04);
    }

    &:focus-visible {
        outline: 2px solid #111;
        outline-offset: 3px;
    }
}

// Revealed with the panel, not with the iframe. Waiting for `--ready` kept it
// invisible for exactly the boot that never becomes ready: `source/` posts
// `fatal` without `ready`, leaving a full-opacity spinner over a scroll-locked
// page and — with the button at `opacity: 0` — no visible way out. This button
// only survives to `--ready` at all when the checkout never claimed the close
// affordance, so it is the failure case that decides when it appears.
.checkout-modal--enter .checkout-modal__close {
    opacity: 1;
}

.checkout-modal__misconfigured {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #555;
    font-size: 0.95rem;
    text-align: center;
    padding: 2rem;
}

.checkout-modal__sr {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
}

@media (prefers-reduced-motion: reduce) {
    .checkout-modal__backdrop,
    .checkout-modal__panel,
    .checkout-modal__iframe,
    .checkout-modal__loading,
    .checkout-modal__close {
        transition: none;
    }
    .checkout-modal__panel {
        transform: none;
    }
}
</style>
